import { config } from '@/shared/config';
import { dateTimeFormat, numberFormat } from '@/shared/lib/intl';

// checker-ignore: i18n.no-hardcoded-persian — numeral table, not user-facing copy
const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'] as const;

/**
 * Persian numerals are produced here and nowhere else — never inline in a
 * component (CLAUDE.md §8.2).
 *
 * Both ways: text the clinic typed on a Persian keyboard (its phone numbers,
 * say) reaches the English page as ۰۷۱… otherwise, so `en` turns it back to 0-9.
 */
export function toLocaleDigits(value: string, locale: string): string {
  if (locale !== 'fa') {
    return toWesternDigits(value);
  }

  return value.replace(/\d/g, (digit) => PERSIAN_DIGITS[Number(digit)] ?? digit);
}

// checker-ignore: i18n.no-hardcoded-persian — numeral tables, not user-facing copy
const PERSIAN_DIGIT_SOURCE = '۰۱۲۳۴۵۶۷۸۹';
// checker-ignore: i18n.no-hardcoded-persian — numeral table, not user-facing copy
const ARABIC_DIGIT_SOURCE = '٠١٢٣٤٥٦٧٨٩';

/**
 * The other direction: whatever numerals were typed, back to `0-9`.
 *
 * A Persian keyboard produces ۰۹۱۲… and an Arabic one ٠٩١٢…, and a patient who
 * typed their own phone number or national ID correctly must not be told it is
 * invalid. The server normalizes too — this is so the client's validation
 * agrees with it rather than rejecting first.
 */
export function toWesternDigits(value: string): string {
  // checker-ignore: i18n.no-hardcoded-persian — numeral range, not user-facing copy
  return value.replace(/[۰-۹٠-٩]/g, (digit) => {
    const persian = PERSIAN_DIGIT_SOURCE.indexOf(digit);

    return String(persian >= 0 ? persian : ARABIC_DIGIT_SOURCE.indexOf(digit));
  });
}

const LTR_ISOLATE = '⁦';
const POP_DIRECTIONAL_ISOLATE = '⁩';

/**
 * Wrap a value in a Unicode LTR isolate so RTL text around it cannot reorder
 * it.
 *
 * A phone number or a masked national ID is not RTL text: it is a left-to-right
 * sequence that happens to contain characters the bidi algorithm treats as
 * neutral (`+`, `*`, `–`, `:`). Dropped raw into a Persian paragraph, those
 * neutrals take the paragraph's direction and `+98917***183` renders as
 * `183***98917+` — the digits stay put, the punctuation jumps to the wrong end,
 * and the number reads as a different number.
 *
 * `dir="ltr"` on an element fixes it too, but only where there *is* an element:
 * values interpolated into a translated sentence have no wrapper of their own.
 * The isolate characters travel with the string, so both cases are covered by
 * the same call.
 */
export function isolateLtr(value: string): string {
  if (value === '') {
    return '';
  }

  return `${LTR_ISOLATE}${value}${POP_DIRECTIONAL_ISOLATE}`;
}

/**
 * The one call for a phone number, national ID, passport, reference code or
 * clock value on screen: localized numerals, and never reordered by RTL text.
 */
export function formatIdentifier(value: string, locale: string): string {
  return isolateLtr(toLocaleDigits(value, locale));
}

/**
 * کد رزرو as the receipt prints it: twelve digits in groups of four, so it can
 * be read out over the phone. Anything that is not twelve digits is shown as
 * given rather than cut up wrongly.
 */
export function formatReceiptCode(code: string, locale: string): string {
  const grouped = /^\d{12}$/.test(code) ? (code.match(/\d{4}/g) ?? [code]).join(' ') : code;

  return formatIdentifier(grouped, locale);
}

/**
 * Rials are stored; Toman is displayed for `fa`. This is a presentation
 * decision — the domain never hears about Toman (CLAUDE.md §3.5, §9).
 */
export function formatMoney(minorAmountInRial: number, locale: string): string {
  const isFa = locale === 'fa';
  const amount = isFa ? Math.round(minorAmountInRial / 10) : minorAmountInRial;
  const formatted = numberFormat(isFa ? 'fa-IR' : 'en-US', {
    maximumFractionDigits: 0,
  }).format(amount);

  return formatted;
}

/**
 * A rate read aloud — «۴٫۵ بیمار در ساعت» — at most one decimal, and none for a
 * whole number, with the locale's own digits and decimal separator.
 */
export function formatDecimal(value: number, locale: string): string {
  return numberFormat(locale === 'fa' ? 'fa-IR' : 'en-US', {
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatTime(date: Date, locale: string): string {
  return dateTimeFormat(locale === 'fa' ? 'fa-IR' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: config.clinicTimezone,
  }).format(date);
}

/**
 * A file size a patient can read: «۱٫۲ مگابایت», not «1258291 bytes».
 *
 * Rounded to one decimal and never below «۱ کیلوبایت», because a lab photo is
 * never actually zero and «۰ کیلوبایت» reads as a failed attachment.
 */
export function formatFileSize(bytes: number, locale = 'fa'): string {
  const kilobytes = bytes / 1024;

  if (kilobytes < 1024) {
    return toLocaleDigits(`${String(Math.max(1, Math.round(kilobytes)))} KB`, locale);
  }

  return toLocaleDigits(`${(kilobytes / 1024).toFixed(1)} MB`, locale);
}
